import * as React from 'react';
import styled from 'styled-components/macro';

export type ComponentProps = {
  disabled?: boolean;
  onClick?: (
    e?: React.SyntheticEvent | React.SyntheticEvent<Element, Event>
  ) => void;
  text: string;
  isFullWidth?: boolean;
  danger?: boolean;
  type?: 'button' | 'submit';
  textPadding?: number | string;
  icon?: any;
  margin?: number | string;
  borderRadius?: number;
  fontWeight?: number;
  fontSize?: number | string;
  fontFamily?: string;
  iconPadding?: number | string;
  iconMargin?: number | string;
  iconFontSize?: number | string;
  iconOpacity?: number;
  backgroundColor?: string;
  border?: string;
  textColor?: string;
  iconColor?: string;
  className?: string;
  height?: number | string;
  width?: number | string;
  center?: boolean;
  title?: string;
  isVisible?: boolean;
  hoverBackgroundColor?: string;
  hoverTextColor?: string;
  minWidth?: number | string;
  padding?: number | string;
  justifyContent?: string;
};

type ButtonProps = {
  isFullWidth?: boolean;
  danger?: boolean;
  margin?: number | string;
  borderRadius?: number;
  fontWeight?: number;
  fontSize?: number | string;
  border?: string;
  backgroundColor?: string;
  height?: number | string;
  width?: number | string;
  minWidth?: number | string;
  isVisible?: boolean;
  hoverBackgroundColor?: string;
  padding?: number | string;
  justifyContent?: string;
};

type TextProps = {
  padding?: number | string;
  fontSize?: number | string;
  fontFamily?: string;
  color?: string;
  withIcon?: boolean;
  center?: boolean;
  hoverTextColor?: string;
  justifyContent?: string;
};

type IconProps = {
  margin?: number | string;
  padding?: number | string;
  fontSize?: number | string;
  opacity?: number;
  color?: string;
  center?: boolean;
};

const Button = styled.button<ButtonProps>`
  display: inline-flex;
  justify-content: ${props =>
    props.justifyContent ? props.justifyContent : 'center'};
  align-items: center;
  padding: ${props => (props.padding ? props.padding : '0')};
  color: ${props => props.theme.colors.secondary[100]};
  ${props =>
    props.fontWeight
      ? `font-weight:${props.fontWeight}`
      : `font-family: 'Work Sans,sans-serif'; font-weight: 700;`};
  background: ${props =>
    props.danger
      ? props.theme.colors.danger[100]
      : props.theme.colors.primary[100]};
  border-radius: ${props => (props.borderRadius ? props.borderRadius : '0')}px;
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  opacity: ${props => props.disabled && '.4'};
  overflow: hidden;
  transition: 0.3s all;
  visibility: ${props => (props.isVisible == false ? 'hidden' : 'visible')};
  width: ${props => (props.isFullWidth && '100%') || 'auto'};
  min-width: ${props => props.minWidth || 'auto'};
  ${props => {
    if (props.isFullWidth && props.height === undefined) return 'height:100%';
    if (typeof props.height === 'number') return `height:${props.height}px`;
    if (typeof props.height === 'string') return `height:${props.height}`;
    return 'height: auto';
  }};

  ${props => {
    if (typeof props.width === 'string') {
      return `width:${props.width};`;
    }
    if (typeof props.width === 'number') {
      return `width: ${props.width}px;`;
    }
  }}

  ${props => {
    if (typeof props.margin === 'string') {
      return `margin: ${props.margin};`;
    }
    if (typeof props.margin === 'number') {
      return `margin: ${props.margin}px;`;
    }
    return 'margin: 0;';
  }}

  ${props => {
    if (props.backgroundColor) {
      return `background-color: ${props.backgroundColor};`;
    }
  }}

  ${props => {
    if (props.border) {
      return `border: ${props.border};`;
    }
    return 'border: none;';
  }}

  &:hover:not(:active) {
    ${props => {
      if (props.disabled) {
        return '';
      }

      if (props.hoverBackgroundColor) {
        return `background: ${props.hoverBackgroundColor};`;
      }

      if (!props.disabled && !props.danger && !props.backgroundColor) {
        return `background: ${props.theme.colors.primaryHover[100]};`;
      }
      if (props.danger) {
        return `background: ${props.theme.colors.danger[100]};`;
      }
      return `opacity: 0.6;`;
    }}
  }

  &:focus {
    outline: 0;
  }
`;

const Text = styled.div<TextProps>`
  display: flex;
  align-items: center;
  line-height: 1.5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  justify-content: ${props =>
    props.justifyContent ? props.justifyContent : 'center'};
  font-family: ${props => {
    if (props.fontFamily) {
      return `${props.fontFamily}`;
    }
    return `'Work Sans', sans-serif`;
  }};

  ${props => {
    if (!props.center) {
      if (props.withIcon) {
        return 'width: 80%;';
      }
      return 'width: 100%;';
    }
  }}

  ${props => {
    if (typeof props.padding === 'string') {
      return `padding:${props.padding};`;
    }
    if (typeof props.padding === 'number') {
      return `padding: 16px ${props.padding}px 16px ${props.padding}px;`;
    }
    return 'padding: 16px;';
  }}

  ${props => {
    if (typeof props.fontSize === 'string') {
      return `font-size:${props.fontSize};`;
    }
    if (typeof props.fontSize === 'number') {
      return `font-size:${props.fontSize}px;`;
    }
    return `font-size: 16px;`;
  }}

${props => {
    if (props.color) {
      return `color:${props.color};`;
    }
  }}

${props => {
    if (props.hoverTextColor) {
      return `&:hover:not(:active) {
      color:${props.hoverTextColor};
    }`;
    }
  }}
`;

const IconWrapper = styled.div<IconProps>`
  display: flex;
  width: ${props => (props.center ? 'auto' : '20%')};

  ${props => {
    if (typeof props.margin === 'string') {
      return `margin: ${props.margin};`;
    }
    if (typeof props.margin === 'number') {
      return `margin: ${props.margin}px;`;
    }
    return 'margin: 0 4px 0 0;';
  }}

  ${props => {
    if (props.color) {
      return `
      color:${props.color};
      opacity: ${typeof props.opacity !== 'undefined' ? props.opacity : '0.5'};
      svg > g > path {
        fill: ${props.color};
      }
      `;
    }
  }}

  ${props => {
    if (typeof props.padding === 'string') {
      return `padding:${props.padding};`;
    }
    if (typeof props.padding === 'number') {
      return `padding: 0 0 0 ${props.padding}px;`;
    }
    return 'padding: 0 0 0 16px;';
  }}

  ${props => {
    if (typeof props.fontSize === 'string') {
      return `font-size:${props.fontSize};`;
    }
    if (typeof props.fontSize === 'number') {
      return `font-size:${props.fontSize}px;`;
    }
    return `font-size: 24px;`;
  }}
`;

export const PrimaryButton = ({
  disabled = false,
  onClick,
  text,
  isFullWidth = false,
  danger = false,
  type = 'button',
  textPadding,
  margin,
  borderRadius,
  fontSize,
  fontWeight,
  fontFamily,
  icon,
  iconFontSize,
  iconPadding,
  iconMargin,
  iconColor,
  iconOpacity,
  backgroundColor,
  textColor,
  border,
  className = '',
  height,
  width,
  center,
  title = '',
  isVisible = true,
  hoverBackgroundColor = '',
  hoverTextColor = '',
  minWidth,
  padding = '',
  justifyContent,
}: ComponentProps) => {
  return (
    <Button
      disabled={disabled}
      onClick={onClick}
      danger={danger}
      type={type}
      isFullWidth={isFullWidth}
      margin={margin}
      borderRadius={borderRadius}
      fontWeight={fontWeight}
      border={border}
      backgroundColor={backgroundColor}
      className={className}
      height={height}
      width={width}
      minWidth={minWidth}
      title={title}
      isVisible={isVisible}
      hoverBackgroundColor={hoverBackgroundColor}
      padding={padding}
      justifyContent={justifyContent}
    >
      {icon && (
        <IconWrapper
          className='icon-wrapper'
          padding={iconPadding}
          fontSize={iconFontSize}
          opacity={iconOpacity}
          color={iconColor}
          margin={iconMargin}
          center={center}
        >
          {icon}
        </IconWrapper>
      )}
      <Text
        className='text'
        padding={textPadding}
        fontSize={fontSize}
        fontFamily={fontFamily}
        color={textColor}
        hoverTextColor={hoverTextColor}
        center={center}
        withIcon={typeof icon !== 'undefined'}
        justifyContent={justifyContent}
      >
        {text}
      </Text>
    </Button>
  );
};
